import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import images from "../../../assets/images";
import type { ColDef } from "ag-grid-community";
import { EquipmentInventory } from "../../../types/api";
import { useMemo, useState, useEffect, useCallback } from "react";
import AddEquipmentModal from "./AddEquipmentModal";
import Modal from "../../../components/modal/Modal";
import axiosInstance from "../../../common/axiosInstance";
import { toast } from "react-toastify";
import {
  deleteEquipmentManagement,
  getAllSuppliers,
  fetchAllById,
} from "../../../service/api";
import { useLocation } from "react-router-dom";
import { usePremisesStore } from "../../../store/store";
import {
  useEquipmentCategoryStore,
  useEquipmentSubcategoryStore,
} from "../../../store/masterDataStore";
import { Trash2 } from "lucide-react";

interface Supplier {
  id: string;
  name: string;
  phoneNumber: string;
  email: string;
  address: string;
}

type SimpleImportModalProps = {
  open: boolean;
  onClose: () => void;
  stateId?: string;
  equipmentCategoryName: string;
  isImporting?: boolean;
  onImport: (formData: FormData) => Promise<void>;
};

const SimpleImportEquipmentModal: React.FC<SimpleImportModalProps> = ({
  open,
  onClose,
  stateId,
  isImporting,
  equipmentCategoryName,
  onImport,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [state_id, setStateId] = useState(stateId || "");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (open) {
      setFile(null);
      setStateId(stateId || "");
      setError("");
    }
  }, [open, stateId]);

  const validate = () => {
    if (!state_id) return "Equipment category is required";
    if (!file) return "Excel file is required";
    const allowedTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
      "application/vnd.ms-excel", // .xls
    ];
    if (!allowedTypes.includes(file.type))
      return "Only Excel (.xlsx, .xls) is allowed";
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) return "File size must be less than 10MB";
    return "";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }

    const formData = new FormData();
    formData.append("equipmentCategoryId", state_id);
    if (file) formData.append("file", file);
    await onImport(formData);
  };

  if (!open) return null;

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      closeButton={true}
      modalClass="bg-white w-full max-w-xl rounded-xl shadow-lg relative max-h-[80vh] flex flex-col"
    >
      <div className="p-6">
        <h2 className="text-xl font-semibold text-gray-800 border-b pb-2 mb-6">
          Import Equipment
        </h2>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Equipment Category<span className="text-red-500">*</span>
            </label>
            <input
              value={equipmentCategoryName}
              readOnly={true}
              onChange={(e) => {
                setStateId(e.target.value);
                if (error) setError("");
              }}
              className="w-full p-2 cursor-default text-medium font-medium text-gray-700 rounded-md"
              placeholder="Enter state_id"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Upload Excel <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => {
                setFile(e.target.files?.[0] || null);
                if (error) setError("");
              }}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none"
            />
            <p className="text-xs text-gray-500">
              Excel (.xlsx, .xls) files up to 10MB
            </p>
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-full hover:bg-gray-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={onClose}
              disabled={isImporting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isImporting}
            >
              {isImporting ? "Uploading..." : "Upload"}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

const EquipmentInventoryAndRegistration: React.FC = ({}) => {
  const location = useLocation();
  const { state } = location;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [selectedEquipment, setSelectedEquipment] =
    useState<EquipmentInventory | null>(null);
  const [mode, setMode] = useState<"create" | "view" | "update">("create");
  const [rowData, setRowData] = useState<EquipmentInventory[]>([]);
  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]); // Add suppliers state
  const { equipmentSubcategory, fetchAllEquipmentSubcategory } =
    useEquipmentSubcategoryStore();
  const { allPremises, fetchPremises } = usePremisesStore();
  const { equipmentCategory, fetchAllEquipmentCategory } =
    useEquipmentCategoryStore();

  useEffect(() => {
    if (location.pathname === "/equipment-management/add-equipment") {
      setMode("create");
      setSelectedEquipment(null);
      setIsModalOpen(true);
    }
  }, [location.pathname]);

  // Fetch premises data and suppliers
  useEffect(() => {
    fetchPremises();
    fetchAllEquipmentCategory();
    fetchAllEquipmentSubcategory();
    fetchSuppliers(); // Fetch suppliers
  }, [fetchPremises, fetchAllEquipmentCategory, fetchAllEquipmentSubcategory]);

  // Function to fetch suppliers
  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await getAllSuppliers();
      setSuppliers(response.data.data || []);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      toast.error("Failed to fetch suppliers");
    }
  }, []);

  const fetchEquipmentData = useCallback(async (equipmentId: string) => {
    try {
      setLoading(true);
      const response = await fetchAllById(equipmentId);
      if (response.status === 200) {
        setRowData(response.data.message as EquipmentInventory[]);
      } else {
        toast.error("Failed to fetch equipment data ");
      }
    } catch (error: any) {
      toast.error("Failed to fetch equipment data");
      console.error("Error fetching equipment data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEquipmentData(state?._id);
  }, [fetchEquipmentData]);

  const getCategoryName = useCallback(
    (categoryId: string) => {
      if (!categoryId || !equipmentCategory.length) return categoryId;
      const category = equipmentCategory.find((cat) => cat._id === categoryId);
      return category ? category.categoryName : categoryId;
    },
    [equipmentCategory],
  );

  const getSubcategoryName = useCallback(
    (subcategoryId: string) => {
      if (!subcategoryId || !equipmentSubcategory.length) return subcategoryId;
      const subcategory = equipmentSubcategory.find(
        (sub) => sub._id === subcategoryId,
      );
      return subcategory ? subcategory.subcategoryName : subcategoryId;
    },
    [equipmentSubcategory],
  );

  const getSupplierName = useCallback(
    (supplierId: string) => {
      if (!supplierId || !suppliers.length) return supplierId;
      const supplier = suppliers.find((sup) => sup.id === supplierId);
      return supplier ? supplier.name : supplierId;
    },
    [suppliers],
  );

  const getPremiseName = useCallback(
    (premiseId: string) => {
      if (!premiseId || !allPremises.length) return premiseId;

      const premise = allPremises.find((p) => p._id === premiseId);
      return premise ? premise.premisesName : premiseId;
    },
    [allPremises],
  );

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Equipment ID",
        field: "equipmentId",
        cellStyle: {
          padding: "10px 10px 0 10px",
        },
      },
      {
        headerName: "Equipment Name",
        field: "equipmentName",
        cellStyle: {
          padding: "10px 10px 0 10px",
        },
      },
      {
        headerName: "Supplier",
        field: "SupplierVendor",
        cellStyle: {
          padding: "10px 10px 0 10px",
        },
        valueGetter: (params) => {
          return getSupplierName(params.data.SupplierVendor);
        },
      },
      {
        headerName: "Category",
        field: "category",
        cellStyle: {
          padding: "10px 10px 0 10px",
        },
        valueGetter: (params) => {
          return getCategoryName(params.data.category);
        },
      },
      {
        headerName: "Sub Category",
        field: "subcategory",
        cellStyle: {
          padding: "10px 10px 0 10px",
        },
        valueGetter: (params) => {
          return getSubcategoryName(params.data.subcategory);
        },
      },
      {
        headerName: "Serial Number",
        field: "serialNumber",
        cellStyle: {
          padding: "10px 10px 0 10px",
        },
      },
      {
        headerName: "Purchase Date",
        field: "purchaseDate",
        cellStyle: {
          padding: "10px 10px 0 10px",
        },
      },
      {
        headerName: "Warranty Expiry",
        field: "warrantyExpiryDate",
        cellStyle: {
          padding: "10px 10px 0 10px",
        },
      },
      {
        headerName: "Status",
        field: "status",
        cellStyle: {
          padding: "10px 10px 0 10px",
        },
        cellRenderer: (params: any) => {
          return (
            <div>
              {params.value === "Active" ? (
                <span className="px-2 py-1 bg-green-200 text-green-800 rounded-full text-md font-medium">
                  Active
                </span>
              ) : params.value === "In Repair" ? (
                <span className="px-2 py-1 bg-yellow-200 text-yellow-600 rounded-full text-md font-medium">
                  In Repair
                </span>
              ) : params.value === "Decommissioned" ? (
                <span className="px-2 py-1 bg-red-200 text-red-700 rounded-full text-md font-medium">
                  Decommissioned
                </span>
              ) : (
                <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-md font-medium">
                  {params.value}
                </span>
              )}
            </div>
          );
        },
      },
      {
        headerName: "Actions",
        cellStyle: {
          padding: "10px 10px 0 10px",
        },
        cellRenderer: (params: any) => {
          return (
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleViewEquipment(params.data)}
                className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                title="View Equipment"
              >
                <img
                  src={images.viewIcon || "/placeholder.svg"}
                  alt="View"
                  className="w-5 h-5"
                />
              </button>
              <button
                onClick={() => handleEditEquipment(params.data)}
                className="p-1 text-green-600 hover:bg-green-100 rounded"
                title="Edit Equipment"
              >
                <img
                  src={images.editIcon || "/placeholder.svg"}
                  alt="Edit"
                  className="w-5 h-5"
                />
              </button>
              <button
                onClick={() => handleDeleteEquipment(params.data._id as string)}
                className="p-1 text-red-600 hover:bg-red-100 rounded text-xs font-medium"
                title="Delete Equipment"
              >
                <Trash2 className="w-5" />
              </button>
            </div>
          );
        },
      },
    ],
    [getPremiseName, getCategoryName, getSubcategoryName, getSupplierName],
  );

  // ... rest of your component remains the same
  const handleAddEquipment = () => {
    setMode("create");
    setSelectedEquipment(null);
    setIsModalOpen(true);
  };

  const handleDownloadTemplate = useCallback(async () => {
    if (!state?.equipmentName) {
      toast.error(
        "Unable to identify equipment category for template download.",
      );
      return;
    }

    setIsDownloading(true);
    try {
      const assetType = state.equipmentName;
      const sanitizedAssetType = assetType
        .replace(/[^a-zA-Z0-9\s]/g, "")
        .replace(/\s+/g, "-")
        .toLowerCase();

      const response = await axiosInstance.get(
        `/api/equipment-management/download-template`,
        {
          responseType: "blob",
          headers: {
            Accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${sanitizedAssetType}_Upload_Template.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading template:", error);
      toast.error("Failed to download template. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  }, [state]);

  const handleImportAssets = useCallback(
    async (formData: FormData) => {
      setIsImporting(true);
      try {
        await axiosInstance.post(
          "/api/equipment-management/upload-excel",
          formData,
        );
        toast.success("Assets imported successfully!");
        fetchEquipmentData(state?._id);
        setIsImportModalOpen(false);
      } catch (error) {
        console.error("Import failed:", error);
        toast.error("Import failed. Please try again.");
      } finally {
        setIsImporting(false);
      }
    },
    [fetchEquipmentData, state?._id],
  );

  const handleViewEquipment = (equipment: EquipmentInventory) => {
    setSelectedEquipment(equipment);
    setMode("view");
    setIsModalOpen(true);
  };

  const handleEditEquipment = (equipment: EquipmentInventory) => {
    setSelectedEquipment(equipment);
    setMode("update");
    setIsModalOpen(true);
  };

  const handleDeleteEquipment = async (_id: string) => {
    if (!_id) {
      toast.error("Invalid equipment ID");
      return;
    }

    if (window.confirm("Are you sure you want to delete this equipment?")) {
      try {
        await deleteEquipmentManagement(_id);
        toast.success("Equipment deleted successfully");
        fetchEquipmentData(state?._id);
      } catch (error: any) {
        toast.error("Failed to delete equipment");
        console.error("Error deleting equipment:", error);
      }
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedEquipment(null);
    setMode("create");

    if (location.pathname === "/equipment-management/add-equipment") {
      window.history.back();
    }
  };

  const handleEquipmentSuccess = () => {
    handleModalClose();
    fetchEquipmentData(state?._id);
  };

  return (
    <div className="px-2 py-4 mt-12 w-full h-full">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-800 mb-6">
          {state?.equipmentName}
        </h1>

        <span className="flex items-center gap-3">
          <button
            className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleDownloadTemplate}
            disabled={isDownloading}
          >
            <img
              src={images.addPremiessIcon || "/placeholder.svg"}
              width={25}
              alt=""
            />
            {isDownloading ? "Downloading..." : "Download template"}
          </button>

          <button
            className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => setIsImportModalOpen(true)}
            disabled={isImporting}
          >
            <img
              src={images.addPremiessIcon || "/placeholder.svg"}
              width={25}
              alt=""
            />
            {isImporting ? "Importing..." : "IMPORT EQUIPMENT"}
          </button>

          <button
            className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
            onClick={handleAddEquipment}
          >
            <img
              src={images.addPremiessIcon || "/placeholder.svg"}
              width={25}
              alt=""
            />
            ADD EQUIPMENT
          </button>
        </span>
      </div>

      <div className="ag-theme-alpine mt-2 bg-white rounded shadow">
        {loading ? (
          <div className="text-center py-8">
            <div className="text-lg">Loading equipment data...</div>
          </div>
        ) : (
          <AgGridReact
            columnDefs={columnDefs}
            rowData={rowData}
            rowHeight={50}
            headerHeight={65}
            suppressCellFocus={true}
            animateRows={true}
            domLayout="autoHeight"
            pagination={true}
            paginationPageSize={10}
            paginationPageSizeSelector={[5, 10, 20]}
            paginationAutoPageSize={false}
            defaultColDef={{
              sortable: true,
              resizable: true,
            }}
          />
        )}
      </div>

      <SimpleImportEquipmentModal
        open={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        stateId={state?._id}
        equipmentCategoryName={state.equipmentName}
        isImporting={isImporting}
        onImport={handleImportAssets}
      />

      {/* Add Equipment Modal */}
      {isModalOpen && (
        <AddEquipmentModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSuccess={handleEquipmentSuccess}
          mode={mode}
          equipmentCategoryId={state._id}
          equipmentId={selectedEquipment?._id}
        />
      )}
    </div>
  );
};

export default EquipmentInventoryAndRegistration;
